/**
 * VistoeraTASK-00637 — Workspace auth matrix (node-select + three-state grants).
 * Reuses TASK-00633 split layout; decisions are INHERIT / ALLOW / DENY.
 */
import { Empty, Select, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { grantKey, type MatrixChannelCell, type MatrixRow } from './authMatrixDraft'
import {
  effectiveAllowedLabel,
  planDecisionLabel,
  workspaceDecisionLabel,
  type WorkspaceDecision,
} from './workspaceAuthMatrixDraft'
import './auth-matrix-table.css'

const DECISION_OPTIONS: Array<{ value: WorkspaceDecision; label: string }> = [
  { value: 'INHERIT', label: '继承' },
  { value: 'ALLOW', label: '允许' },
  { value: 'DENY', label: '禁止' },
]

export type WorkspaceAuthMatrixTableProps = {
  rows: MatrixRow[]
  draft: Record<string, WorkspaceDecision>
  baseline: Record<string, WorkspaceDecision>
  selectedNodeId: string | null
  onSelectNode: (nodeId: string) => void
  onDecisionChange: (kind: 'node' | 'channel', subjectId: string, decision: WorkspaceDecision) => void
  loading?: boolean
  readOnly?: boolean
}

function metaLine(planDecision: string | null | undefined, override: WorkspaceDecision, effective?: boolean) {
  return (
    <div className="amx-ws-meta">
      <span>套餐：{planDecisionLabel(planDecision)}</span>
      <span>租户：{workspaceDecisionLabel(override)}</span>
      <span>
        最终：
        {effectiveAllowedLabel(effective)}
        {override !== 'INHERIT' && effective === undefined ? '' : null}
      </span>
    </div>
  )
}

function ChannelPanel({
  row,
  draft,
  baseline,
  onDecisionChange,
  readOnly,
}: {
  row: MatrixRow
  draft: Record<string, WorkspaceDecision>
  baseline: Record<string, WorkspaceDecision>
  onDecisionChange: (kind: 'node' | 'channel', subjectId: string, decision: WorkspaceDecision) => void
  readOnly?: boolean
}) {
  const channels = row.channels || []
  if (channels.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="当前节点暂无已映射的执行通道"
        style={{ margin: '24px 0' }}
      />
    )
  }

  return (
    <div className="amx-channel-panel-scroll">
      <div className="amx-ws-channel-list">
        {channels.map((ch: MatrixChannelCell) => {
          const key = grantKey('channel', ch.subject_id)
          const decision = draft[key] ?? 'INHERIT'
          const dirty = (baseline[key] ?? 'INHERIT') !== decision
          const globalOk = Boolean(ch.globally_available) && ch.grantable !== false
          const label = ch.display_name || ch.subject_id
          return (
            <div key={ch.subject_id} className={`amx-ws-channel-row${dirty ? ' is-dirty' : ''}`}>
              <div className="amx-ws-channel-main">
                <span className="amx-channel-chip-label" title={label}>
                  {label}
                </span>
                {!globalOk ? <Tag>全站已关闭</Tag> : null}
                {dirty ? <Tag color="orange">未保存</Tag> : null}
              </div>
              {metaLine(ch.plan_decision, decision, ch.effective_allowed)}
              <Select
                size="small"
                style={{ width: 110 }}
                value={decision}
                disabled={readOnly || !globalOk}
                options={DECISION_OPTIONS}
                onChange={(v) => onDecisionChange('channel', ch.subject_id, v)}
                getPopupContainer={() => document.body}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function WorkspaceAuthMatrixTable({
  rows,
  draft,
  baseline,
  selectedNodeId,
  onSelectNode,
  onDecisionChange,
  loading,
  readOnly,
}: WorkspaceAuthMatrixTableProps) {
  const selectedRow = useMemo(
    () => rows.find((r) => r.node.subject_id === selectedNodeId) ?? null,
    [rows, selectedNodeId],
  )

  const columns: ColumnsType<MatrixRow> = useMemo(
    () => [
      {
        // TASK-00670 — catalog display_name; never raw category (production/content).
        title: '节点类型',
        key: 'node',
        width: 168,
        ellipsis: true,
        render: (_: unknown, row) => {
          const selected = row.node.subject_id === selectedNodeId
          const label = (row.node.display_name || '').trim() || row.node.subject_id
          return (
            <button
              type="button"
              className={`amx-node-select${selected ? ' amx-node-select-active' : ''}`}
              onClick={() => onSelectNode(row.node.subject_id)}
              title={label}
              data-node-id={row.node.subject_id}
            >
              <span className="amx-node-name">{label}</span>
              {!row.node.globally_available ? (
                <Typography.Text type="secondary" className="amx-node-hint">
                  全站已关闭
                </Typography.Text>
              ) : null}
            </button>
          )
        },
      },
      {
        title: '租户授权',
        key: 'node_decision',
        width: 200,
        render: (_: unknown, row) => {
          const key = grantKey('node', row.node.subject_id)
          const decision = draft[key] ?? 'INHERIT'
          const dirty = (baseline[key] ?? 'INHERIT') !== decision
          const globalOk = Boolean(row.node.globally_available) && row.node.grantable !== false
          return (
            <div className="amx-ws-node-cell" onClick={(e) => e.stopPropagation()}>
              {metaLine(row.node.plan_decision, decision, row.node.effective_allowed)}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Select
                  size="small"
                  style={{ width: 110 }}
                  value={decision}
                  disabled={readOnly || !globalOk}
                  options={DECISION_OPTIONS}
                  onChange={(v) => onDecisionChange('node', row.node.subject_id, v)}
                  getPopupContainer={() => document.body}
                />
                {dirty ? <Tag color="orange">未保存</Tag> : null}
              </div>
              {!globalOk ? (
                <Tooltip title="全站已关闭，Workspace ALLOW 无法绕过">
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    全站禁用优先
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </div>
          )
        },
      },
    ],
    [baseline, draft, onDecisionChange, onSelectNode, readOnly, selectedNodeId],
  )

  return (
    <div className="amx-split">
      <div className="amx-split-nodes" style={{ flex: '0 0 440px', maxWidth: 480, minWidth: 380 }}>
        <Table
          className="amx-table amx-table-nodes"
          size="small"
          rowKey={(r) => r.node.subject_id}
          loading={loading}
          pagination={false}
          scroll={{ y: 420 }}
          columns={columns}
          dataSource={rows}
          rowClassName={(row) => (row.node.subject_id === selectedNodeId ? 'amx-row-selected' : '')}
          onRow={(row) => ({
            onClick: () => onSelectNode(row.node.subject_id),
          })}
          locale={{ emptyText: '暂无功能节点目录' }}
        />
      </div>
      <div className="amx-split-channels">
        <div className="amx-channels-header">
          <Typography.Text strong>
            {selectedRow
              ? `${selectedRow.node.display_name || selectedRow.node.subject_id} · 可用通道`
              : '可用通道'}
          </Typography.Text>
          <Typography.Text type="secondary" className="amx-channels-hint">
            {selectedRow
              ? '仅显示已映射到本节点的 Provider；继承 = 使用套餐默认'
              : '请点击左侧功能节点以配置通道授权'}
          </Typography.Text>
        </div>
        {selectedRow ? (
          <ChannelPanel
            row={selectedRow}
            draft={draft}
            baseline={baseline}
            onDecisionChange={onDecisionChange}
            readOnly={readOnly}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="选择功能节点后显示对应通道"
            style={{ margin: '40px 0' }}
          />
        )}
      </div>
    </div>
  )
}
